import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { initiateLogin } from '@/api/auth'
import { Logo } from '@/components/ui/Logo'

export default function LoginPage() {
  const [signing, setSigning] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)

  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setSigning(false)
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  const handleSignIn = () => {
    setSigning(true)
    initiateLogin()
  }

  return (
    <div className="cg-login">
      <div className="cg-login-bg">
        <div className="cg-login-glow cg-login-glow-1"></div>
        <div className="cg-login-glow cg-login-glow-2"></div>
        <div className="cg-login-grid"></div>
      </div>

      {/* Top bar */}
      <header className="cg-login-topbar">
        <div className="cg-login-mark">
          <Logo size={22} />
          <span>Common Ground</span>
        </div>
      </header>

      {/* Hero */}
      <main className="cg-login-main">
        <div className="cg-login-hero">
          <div className="cg-login-eyebrow">
            <span className="cg-login-pulse"></span>
            Built on the Steam Web API
          </div>
          <h1 className="cg-login-title">
            The wishlist for <em>your group</em>,
            <br />
            not just for you.
          </h1>
          <p className="cg-login-lede">
            Connect your Steam account. Pull in your friends. Build a shared wishlist that knows who
            already owns what — and what everyone wants to play together next.
          </p>

          <div className="cg-login-cta-row">
            <button
              className="cg-login-cta"
              onClick={handleSignIn}
              disabled={signing}
            >
              <span className="cg-login-cta-content">
                {signing ? (
                  <>
                    <span className="cg-login-spinner"></span>
                    <span>Redirecting to Steam…</span>
                  </>
                ) : (
                  <>
                    <SteamGlyph />
                    <span className="cg-login-cta-label">
                      <span className="cg-login-cta-small">Sign in through</span>
                      <span className="cg-login-cta-big">Steam</span>
                    </span>
                    <svg
                      className="cg-login-cta-arrow"
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M3 8h10M9 4l4 4-4 4"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </>
                )}
              </span>
            </button>
            <p className="cg-login-cta-fineprint">
              We use Steam's OpenID. We never see your password.
            </p>
          </div>

          <div className="cg-login-trust">
            <div className="cg-login-trust-item">
              <Lock /> <span>Read-only access</span>
            </div>
            <div className="cg-login-trust-divider"></div>
            <div className="cg-login-trust-item">
              <Eye /> <span>Public profile data only</span>
            </div>
          </div>
        </div>

        {/* Right side: animated visual */}
        <aside className="cg-login-visual">
          <LoginVisual />
        </aside>
      </main>

      {/* Bottom strip: how it works */}
      <section className="cg-login-how" id="how">
        <div className="cg-login-how-step">
          <div className="cg-login-how-num">01</div>
          <div className="cg-login-how-body">
            <h3>Sign in with Steam</h3>
            <p>
              One-click OpenID. Your Steam ID and public profile only — nothing private, no
              passwords.
            </p>
          </div>
        </div>
        <div className="cg-login-how-step">
          <div className="cg-login-how-num">02</div>
          <div className="cg-login-how-body">
            <h3>Pull in your friends</h3>
            <p>We sync your Steam friends list so you can see who's available and what they own.</p>
          </div>
        </div>
        <div className="cg-login-how-step">
          <div className="cg-login-how-num">03</div>
          <div className="cg-login-how-body">
            <h3>Build a shared wishlist</h3>
            <p>Group up. Add games. Vote. See ownership. Find your next co-op night in seconds.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="cg-login-footer">
        <div className="cg-login-footer-left">
          <button
            onClick={() => setShowPrivacy(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            Privacy & Security
          </button>
        </div>
        <div className="cg-login-footer-right">
          Not affiliated with Valve Corporation. Steam and the Steam logo are trademarks of Valve.
        </div>
      </footer>

      {showPrivacy && <PrivacyModal onClose={() => setShowPrivacy(false)} />}
    </div>
  )
}

function PrivacyModal({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  return createPortal(
    <div className="cg-modal-backdrop" onClick={onClose}>
      <div
        className="cg-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 480, maxHeight: 'none' }}
      >
        <div className="cg-modal-header">
          <h2>Privacy & Security</h2>
          <button className="cg-icon-btn cg-modal-close-x" onClick={onClose}>
            ✕
          </button>
        </div>
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <section>
            <h3
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--fg-0)',
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Lock /> Steam Authentication
            </h3>
            <p style={{ fontSize: 13, color: 'var(--fg-1)', lineHeight: 1.5 }}>
              We use Steam's official OpenID 2.0 service. When you sign in, you are redirected to
              Steam's own website. <strong>We never see, handle, or store your password.</strong>
            </p>
          </section>

          <section>
            <h3
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--fg-0)',
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Eye /> Data Access
            </h3>
            <p style={{ fontSize: 13, color: 'var(--fg-1)', lineHeight: 1.5 }}>
              We only request public profile data via the Steam Web API:
            </p>
            <ul
              style={{
                fontSize: 13,
                color: 'var(--fg-1)',
                lineHeight: 1.5,
                marginTop: 8,
                paddingLeft: 20,
              }}
            >
              <li>Your Steam ID and profile name</li>
              <li>Your public library of games</li>
              <li>Your public friends list (to build groups)</li>
            </ul>
          </section>

          <section>
            <h3
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--fg-0)',
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Bolt /> Your Control
            </h3>
            <p style={{ fontSize: 13, color: 'var(--fg-1)', lineHeight: 1.5 }}>
              We do not sell your data or use tracking cookies for advertising.
            </p>
          </section>
        </div>
        <div className="cg-modal-footer">
          <button className="cg-btn primary" onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

// ── Inline icons ──────────────────────────────────────────────────────────
function SteamGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2C6.6 2 2.2 6.1 2 11.3l5.3 2.2c.5-.3 1-.5 1.6-.5h.2l2.4-3.5v-.1a3.7 3.7 0 1 1 3.7 3.7h-.1l-3.4 2.4v.1a2.9 2.9 0 0 1-5.7.6L2 14.7C3 18.9 7.1 22 12 22c5.5 0 10-4.5 10-10S17.5 2 12 2z M7.6 18.2c.7.3 1.5 0 1.8-.7.3-.7 0-1.5-.7-1.8l-1.2-.5c.5.7 1.4 1 2.1.7.6-.3.9-.9.7-1.5l-2.7 1.1zm9.5-7.7c0-1.4-1.1-2.5-2.5-2.5s-2.5 1.1-2.5 2.5 1.1 2.5 2.5 2.5 2.5-1.1 2.5-2.5z" />
    </svg>
  )
}
function Lock() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="7" width="10" height="7" rx="1.5" />
      <path d="M5 7V5a3 3 0 0 1 6 0v2" />
    </svg>
  )
}
function Eye() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1.5 8s2.4-4.5 6.5-4.5S14.5 8 14.5 8s-2.4 4.5-6.5 4.5S1.5 8 1.5 8z" />
      <circle cx="8" cy="8" r="1.8" />
    </svg>
  )
}
function Bolt() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 1L3 9h4l-1 6 6-8H8l1-6z" />
    </svg>
  )
}

// ── Animated right-side visual ────────────────────────────────────────────
// Stylized "shared wishlist" stack with three friend avatars and connecting lines.
function LoginVisual() {
  return (
    <div className="cg-loginvis">
      {/* Connection lines (svg under cards) */}
      <svg className="cg-loginvis-lines" viewBox="0 0 400 480" preserveAspectRatio="none">
        <defs>
          <linearGradient id="cg-line-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0" />
            <stop offset="50%" stopColor="var(--accent)" stopOpacity="0.55" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M70 80 Q 200 180 330 80" stroke="url(#cg-line-grad)" strokeWidth="1.2" fill="none" />
        <path d="M70 80 Q 130 280 200 400" stroke="url(#cg-line-grad)" strokeWidth="1.2" fill="none" />
        <path d="M330 80 Q 270 280 200 400" stroke="url(#cg-line-grad)" strokeWidth="1.2" fill="none" />
      </svg>

      {/* Three friend avatars at corners */}
      <div className="cg-loginvis-avatar cg-loginvis-avatar-1">
        <div className="cg-loginvis-ring cg-loginvis-ring-online">
          <SteamLikeAvatar name="Maya" variant="blue" />
        </div>
        <div className="cg-loginvis-name">Maya</div>
      </div>
      <div className="cg-loginvis-avatar cg-loginvis-avatar-2">
        <div className="cg-loginvis-ring cg-loginvis-ring-online">
          <SteamLikeAvatar name="Jules" variant="amber" />
        </div>
        <div className="cg-loginvis-name">Jules</div>
      </div>
      <div className="cg-loginvis-avatar cg-loginvis-avatar-3">
        <div className="cg-loginvis-ring cg-loginvis-ring-public">
          <SteamLikeAvatar name="Ren" variant="violet" />
        </div>
        <div className="cg-loginvis-name">Ren</div>
      </div>

      {/* Center card stack — wishlist preview */}
      <div className="cg-loginvis-stack">
        <div className="cg-loginvis-card cg-loginvis-card-bg2"></div>
        <div className="cg-loginvis-card cg-loginvis-card-bg1"></div>
        <div className="cg-loginvis-card cg-loginvis-card-front">
          <div
            className="cg-loginvis-card-cover"
          >
            <img
              src="https://cdn.akamai.steamstatic.com/steam/apps/553850/header.jpg"
              alt="Helldivers II"
              className="cg-loginvis-card-cover-img"
            />
            <div className="cg-loginvis-card-cover-grain"></div>
          </div>
          <div className="cg-loginvis-card-body">
            <div className="cg-loginvis-card-row">
              <div className="cg-loginvis-card-title">Helldivers II</div>
              <div className="cg-loginvis-card-price">
                <span className="cg-loginvis-card-sale">−25%</span>
                <span className="cg-loginvis-card-newprice">$29.99</span>
              </div>
            </div>
            <div className="cg-loginvis-card-meta">
              <div className="cg-loginvis-card-owners">
                <span className="cg-loginvis-card-owners-label">Owned by</span>
                <div className="cg-loginvis-card-mini" data-hue="200">
                  <SteamLikeAvatar name="Maya" variant="blue" compact />
                </div>
                <div className="cg-loginvis-card-mini cg-loginvis-card-mini-ghost" data-hue="38">
                  <SteamLikeAvatar name="Jules" variant="amber" compact />
                </div>
                <div className="cg-loginvis-card-mini cg-loginvis-card-mini-ghost" data-hue="268">
                  <SteamLikeAvatar name="Ren" variant="violet" compact />
                </div>
              </div>
              <div className="cg-loginvis-card-vote">
                <span className="cg-loginvis-card-vote-up">▲ 2</span>
                <span className="cg-loginvis-card-vote-dn">▼ 0</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating chips (animated) */}
      <div className="cg-loginvis-chip cg-loginvis-chip-1">
        <span className="cg-loginvis-dot" style={{ background: 'oklch(0.78 0.18 158)' }}></span>
        Maya added Hades II
      </div>
      <div className="cg-loginvis-chip cg-loginvis-chip-2">Jules upvoted Lethal Company</div>
      <div className="cg-loginvis-chip cg-loginvis-chip-3">Ren ranked Stardew 5★</div>
    </div>
  )
}

function SteamLikeAvatar({
  name,
  variant,
  compact = false,
}: {
  name: string
  variant: 'blue' | 'amber' | 'violet'
  compact?: boolean
}) {
  return (
    <div className="cg-loginvis-face" data-avatar={variant} aria-label={name}>
      <span className="cg-loginvis-face-shape cg-loginvis-face-shape-a"></span>
      <span className="cg-loginvis-face-shape cg-loginvis-face-shape-b"></span>
      {!compact && <span className="cg-loginvis-face-initial">{name[0]}</span>}
    </div>
  )
}
